import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import Card from './ui/Card';
import Button from './ui/Button';
import Icon from './ui/Icon';

/**
 * ProfileCompletionGuard - Shows "Complete Profile" message for incomplete Google users
 *
 * Usage:
 * ```jsx
 * const content = (
 *   <ProfileCompletionGuard>
 *     <YourPageContent />
 *   </ProfileCompletionGuard>
 * );
 * ```
 */
const ProfileCompletionGuard = ({ children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Check if user is Google user with incomplete profile
  const needsProfileCompletion = user?.is_google_user && !user?.profile_completed;

  // If profile is complete or not Google user, render children normally
  if (!needsProfileCompletion) {
    return children;
  }

  // Show profile completion required message
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 pt-20 pb-12 px-4">
      <div className="max-w-2xl mx-auto">
        <Card className="text-center p-8">
          <div className="mb-6">
            <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="user" className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {t('profile.completeProfileRequired') || 'প্রোফাইল সম্পূর্ণ করুন'}
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              {t('profile.completeProfileMessage') ||
               'এই বৈশিষ্ট্য ব্যবহার করার আগে আপনার প্রোফাইল সম্পূর্ণ করুন। আপনার ফোন নম্বর এবং অন্যান্য তথ্য প্রদান করুন।'}
            </p>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
              {t('profile.whatYouNeed') || 'আপনার প্রয়োজন:'}
            </h3>
            <ul className="text-left text-gray-700 dark:text-gray-300 space-y-2">
              <li className="flex items-center">
                <Icon name="check-circle" className="w-5 h-5 text-green-500 mr-2" />
                {t('profile.phoneNumber') || 'ফোন নম্বর'}
              </li>
              <li className="flex items-center">
                <Icon name="check-circle" className="w-5 h-5 text-green-500 mr-2" />
                {t('profile.password') || 'পাসওয়ার্ড'}
              </li>
              <li className="flex items-center">
                <Icon name="check-circle" className="w-5 h-5 text-green-500 mr-2" />
                {t('profile.gender') || 'লিঙ্গ'}
              </li>
              <li className="flex items-center">
                <Icon name="check-circle" className="w-5 h-5 text-green-500 mr-2" />
                {t('profile.country') || 'দেশ'}
              </li>
            </ul>
          </div>

          <Button
            onClick={() => navigate('/auth/complete-profile')}
            variant="primary"
            size="lg"
            className="w-full"
          >
            <Icon name="edit" className="w-5 h-5 mr-2" />
            {t('profile.completeProfileButton') || 'প্রোফাইল সম্পূর্ণ করুন'}
          </Button>
        </Card>
      </div>
    </div>
  );
};

export default ProfileCompletionGuard;
